// The delta-v map's words and figures, shared by the slim panel
// (src/hudDvMap.ts) and its wide window (src/hudDvTable.ts), so a figure reads
// the same in both places. No DOM here beyond the tooltip helper and the
// flight-plan list both of them paint.

import { autopilotFlightPlan, canAutopilotFly } from './sim/autopilot';
import type { AutopilotPhase, AutopilotPlanStep, AutopilotStep } from './sim/autopilot';
import type { DvMapMoved } from './sim/dvMap';
import type { BurnSense, TransferBurn, TransferPlan } from './sim/transfer';
import { J2000_JD, earthDateMsFromT } from './world/clock';
import { TEST_SWITCHES } from './urlFlags.ts';

/** What each dated step of the plan is called, in the player's own words: the
 *  NEXT STEP row names the one the warp button jumps the clock to. */
export const STEP_WORDS: Readonly<Record<AutopilotStep, string>> = {
  window: 'Departure window',
  'course-fix': 'Course fix',
  arrival: 'Arrival',
};

/** Each phase in the player's own words, since `AutopilotPhase` is the state
 *  machine's vocabulary and not anything to put on a panel. */
export const PHASE_WORDS: Readonly<Record<AutopilotPhase, string>> = {
  wait: 'waiting for the window',
  depart: 'burning to leave',
  coast: 'coasting',
  trim: 'correcting the course',
  capture: 'braking to arrive',
  arrived: 'arrived',
  handback: 'you have the stick',
  refused: 'stopped',
};

/** The open-space line of an expanded row. */
export const FREE_TIP = 'The two pushes with no world to climb out of or drop into. The SPEED CHANGE total adds those two costs.';
/** The instant-push figure the departure carries beside what the burn costs. */
export const INSTANT_TIP = 'What leaving would cost as one instant push. The LEAVE figure is higher because the real burn runs for hours against the pull of the world.';
/** The flat figure the row carries beside the real one. */
export const TEXTBOOK_TIP = 'The same trip with both worlds on circular, flat orbits. The real orbits are tilted and elliptical, so the SPEED CHANGE total differs.';
/** A row still carrying the last survey's price while this one is solved. */
export const WORKING_TIP = 'Still being priced for the new date. This is the last date\'s cost.';
/** The departure and arrival dates of an expanded row. */
export const WHEN_TIP = 'The day the ship leaves and the day it arrives. Leaving on another day costs more than the SPEED CHANGE.';
/** The two sub-heads under the list. */
export const SKIPPED_TIP = 'Places this map will not plan a trip to now, with the reason for each.';
/** Why there is nothing to plan, when the panel has to say so. */
export const NOTE_TIP = 'Why the map has no trips to show right now.';
/** The line that says the map has given up the crossing that was picked. */
export const MOVED_TIP = 'The window you picked has passed. The row now shows the next crossing to the same place, and GO and WARP TO NEXT use it.';
/** One line per burn, by what the burn is. */
export const BURN_TIPS: Readonly<Record<TransferBurn['kind'], string>> = {
  departure: 'Leaves your orbit onto the crossing.',
  capture: 'Brakes you into the circular orbit ARRIVE IN picks, LOW or HIGH.',
};
/** The other kind of arrival: a rock too small to hold an orbit at all. */
export const MATCH_TIP = 'Stops you beside a world too small to orbit.';
/** The wait, and the speed the arrival push has to take off the ship. */
export const WAIT_TIP = 'How long you wait here for the destination to come round to where the crossing ends. Leave early and you arrive where it was.';
export const RELATIVE_TIP = 'Your speed relative to the destination when you arrive. The arrival push takes it off; until then you are flying past at this speed.';
export const NO_GO_TIP = 'GO is not offered for this trip, because the autopilot has no course for it. Fly this one yourself.';
/** The plane column of the wide window, before any trip fills it. */
export const PLANE_TIP = 'For a trip to a moon: your orbit\'s tilt from the moon\'s, what the turn costs, which push pays it, and whether GO can fly it.';

/**
 * What GO will do on a trip inside one world's pull, which is every trip to a
 * moon of the world the ship is at.
 *
 * The line names the push that pays for the turn. A turn priced at the leaving
 * push is flown there, where the ship's orbit crosses the moon's plane; one
 * priced at the braking push is closed with the rest of the speed left between
 * the ship and the moon.
 */
export function moonGoTip(
  destinationName: string, end: 'departure' | 'arrival' | null, totalDvKmS: number,
): string {
  const paid = end === 'departure' ? 'leaving' : 'braking';
  return `GO flies the whole crossing to ${destinationName}. The ${paid} push pays for the turn into its plane, `
    + `so the trip costs about ${kms(totalDvKmS)} plus course fixes.`;
}

/** The tilt line of a trip to a moon, which names both planes it is between. */
export function tiltTip(destinationName: string, tiltDeg: number): string {
  return `Your orbit is tilted ${tiltDeg.toFixed(1)} degrees from ${destinationName}'s. `
    + 'What the turn costs is in the SPEED CHANGE total.';
}

/** The turn line, which says which push the tilt is paid in. The search prices
 *  both ends of the crossing and keeps whichever comes out cheaper, so this is
 *  not always the slow end; a leg priced at the leaving push also carries that
 *  push's own path and phasing difference from the flat trip, not the turn
 *  alone. */
export function turnTip(destinationName: string, end: 'departure' | 'arrival'): string {
  const paidAt = end === 'departure' ? 'leaving' : `arriving at ${destinationName}`;
  const base = `What turning into ${destinationName}'s plane adds, paid ${paidAt}, the cheaper end to turn at.`;
  if (end !== 'departure') return base;
  return `${base} It also carries the flat trip's path difference.`;
}

/**
 * The control (`?dvmaptofraw=1`): put back the pre-fix formatter that never
 * converts to years, so a far destination's travel time reads as a raw day
 * count ("16958 days") instead of "46.4 yr".
 */
const RAW_TOF_YEARS = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmaptofraw') === '1';

/**
 * The control (`?moongowithheld=1`): put back the gate that withheld GO from
 * every plan carrying a tilt, which is every trip to a moon. The row says the
 * trip is yours to fly and no button appears, the way the panel shipped before
 * the guidance could fly one.
 */
const MOON_GO_WITHHELD = TEST_SWITCHES && new URLSearchParams(location.search).get('moongowithheld') === '1';

/** Whether GO is offered for a plan. The panel's button and `src/main.ts`'s own
 *  refusal off it read the same answer, so a button cannot appear on a trip the
 *  guidance would then decline to start. */
export function goOffered(plan: TransferPlan): boolean {
  if (MOON_GO_WITHHELD && plan.planeTiltDeg !== null) return false;
  return canAutopilotFly(plan);
}

/** Give an element the HUD's styled tooltip. One place, so an element cannot
 *  end up with the class and no text or with text and no class. */
export function tip(el: HTMLElement, text: string): void {
  el.classList.add('hud-tip');
  el.dataset['tooltip'] = text;
}

// The two glyphs the rest of the HUD draws, by code point so this file stays
// plain ASCII: the em dash every panel uses for "nothing to show", and the
// arrow between a departure and an arrival date.
export const NOTHING = String.fromCodePoint(0x2014);
export const TO = String.fromCodePoint(0x2192);

/** Kilometres a second, to the digit the written figures are quoted at. */
export function kms(value: number): string {
  return `${value.toFixed(3)} km/s`;
}

/** Every duration cell goes through this one formatter, so a count past what
 *  "N days" can say in a narrow column (Pluto's own time of flight is 16958
 *  days) reads in years instead rather than clipping mid-word. Under a day it
 *  reads in hours and minutes, which is the whole scale of a trip to a moon:
 *  Mars to Phobos is a 2.3 hour crossing. */
export function days(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 min';
  if (!RAW_TOF_YEARS && value > 999) return `${(value / 365.25).toFixed(1)} yr`;
  if (value >= 10) return `${Math.round(value)} days`;
  if (value >= 1) return `${value.toFixed(1)} days`;
  const hours = value * 24;
  if (hours >= 1) {
    const roundedHours = Math.round(hours * 10) / 10;
    return roundedHours >= 24 ? '1.0 days' : `${roundedHours.toFixed(1)} h`;
  }
  const minutes = Math.round(hours * 60);
  return minutes >= 60 ? '1.0 h' : `${minutes} min`;
}

/** Days inside which a window reads "now" rather than as a count. One minute:
 *  a moon's window comes round in a couple of hours, and the old hour-and-a-bit
 *  floor read most of one as no wait at all. */
const WAIT_NOW_DAYS = 1 / 1440;

/** How long until a window, in the player's own words. */
export function waitText(value: number): string {
  if (value <= WAIT_NOW_DAYS) return 'now';
  return days(value);
}

/** A speed change in whichever unit reads without leading zeros. */
export function dvFigure(mps: number): string {
  if (!Number.isFinite(mps) || mps <= 0) return '0 m/s';
  if (mps >= 1000) return kms(mps / 1000);
  return `${mps.toFixed(0)} m/s`;
}

/** A burn's own owing, blank where there is no burn to owe on. */
export function dvText(mps: number): string {
  if (!Number.isFinite(mps) || mps <= 0) return NOTHING;
  return dvFigure(mps);
}

/** What the trip has spent against what the map priced it at, one formatter so
 *  the two figures cannot land in different units. */
export function spentText(spentMps: number, pricedMps: number): string {
  return `${dvFigure(spentMps)}, map priced ${dvFigure(pricedMps)}`;
}

/** Simulated seconds to the next event, in the largest unit that still says
 *  something: a 780-day window and a 90-second slew go through this one row. */
export function durationText(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return NOTHING;
  if (seconds < 90) return `${Math.round(seconds)} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${(seconds / 3600).toFixed(1)} hr`;
  return days(seconds / 86400);
}

/** Game time as TRIP TIME counts it, whole units, two at most: "40 s", "12 min",
 *  "3 h 12 min", "212 d 4 h", "1 yr 47 d". */
export function tripClockText(seconds: number): string {
  const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const two = (big: number, bigUnit: string, small: number, smallUnit: string): string =>
    (small > 0 ? `${big} ${bigUnit} ${small} ${smallUnit}` : `${big} ${bigUnit}`);
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86_400) return two(Math.floor(s / 3600), 'h', Math.floor((s % 3600) / 60), 'min');
  const year = 365.25 * 86_400;
  if (s < year) return two(Math.floor(s / 86_400), 'd', Math.floor((s % 86_400) / 3600), 'h');
  const y = Math.floor(s / year);
  return two(y, 'yr', Math.floor((s - y * year) / 86_400), 'd');
}

/** How long a push lasts at the thrust the ship actually has. The map prices
 *  instant pushes; at 0.49 m/s2 the departure for Phobos runs ten minutes and
 *  the one that matches its velocity twenty, which is the difference between a
 *  number to read and a number to fly. */
export function burnText(dvKmS: number, accelMps2: number): string | null {
  if (!Number.isFinite(accelMps2) || accelMps2 <= 0) return null;
  const seconds = (dvKmS * 1000) / accelMps2;
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 60) {
    const whole = Math.round(seconds);
    return whole >= 60 ? '1 min' : `${whole} s`;
  }
  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);
    return minutes >= 60 ? '1.0 h' : `${minutes} min`;
  }
  return `${(seconds / 3600).toFixed(1)} h`;
}

export function dateText(jd: number): string {
  const ms = earthDateMsFromT((jd - J2000_JD) * 86400);
  if (!Number.isFinite(ms)) return NOTHING;
  return new Date(ms).toISOString().slice(0, 10);
}

/** A moon's own name where the registry label is `<Planet> <numeral> (<Name>)`;
 *  every other label is unchanged. Only displayed text goes through this - ids,
 *  keys and tooltips keep the full label. */
export function plainBodyName(label: string): string {
  const match = /^[A-Z][a-z]+ [IVXL]+ \((.+)\)$/.exec(label);
  return match?.[1] ?? label;
}

/**
 * The line written when the map has given up the crossing that was picked:
 * which trip went without the ship, and what the one in its place costs
 * against it. `tools/preview/descent-dojo.mjs --transit` reads these words.
 */
export function movedRowText(moved: DvMapMoved): string {
  const change = Math.abs(moved.dvChangeKmS) < 0.001
    ? 'for the same speed change'
    : moved.dvChangeKmS < 0
      ? `for ${kms(-moved.dvChangeKmS)} less`
      : `for ${kms(moved.dvChangeKmS)} more`;
  // A gap against the armed date, not a second calendar date: the floor lets
  // the two departures land on the same UTC day, where two bare dates would
  // print identically.
  const gapDays = moved.departureJd - moved.armedDepartureJd;
  return `the crossing to ${plainBodyName(moved.destinationName)} you picked left on `
    + `${dateText(moved.armedDepartureJd)} without you; the next one leaves `
    + `${days(gapDays)} later, ${change}`;
}

export function heightText(km: number | null): string {
  if (km === null) return '';
  if (km >= 1e6) return `${(km / 1e6).toFixed(2)} million km up`;
  if (km >= 1000) return `${Math.round(km).toLocaleString('en-US')} km up`;
  return `${km.toFixed(0)} km up`;
}

/** Which way the ship points the engine, in the player's own words. The
 *  solver's `sense` is a DIRECTION against the ship's own motion, so these say
 *  where the push goes and not what happens to the speed: off a closed orbit a
 *  departure leaves the ship faster whichever way it pointed. */
export const SENSE_WORDS: Readonly<Record<BurnSense, string>> = {
  prograde: 'push forward',
  retrograde: 'push back',
  across: 'push sideways',
};

/** Below this share of full thrust the tooltip says the radiators stretch the burn. */
const STRETCHED_SHARE = 0.995;

/** A burn's tooltip: what it is, which way, and how long the engine is on for
 *  it at the thrust a trip flies (`accelMps2`, already `share` of full). */
export function burnTip(burn: TransferBurn, accelMps2: number, share = 1): string {
  const label = `${burn.label} (${SENSE_WORDS[burn.sense]})`;
  const held = burnText(burn.dvKmS, accelMps2);
  const isMatch = burn.kind === 'capture' && burn.frame === 'heliocentric';
  const carriesTheClimb = burn.dvKmS > burn.impulseDvKmS;
  const lasts = share < STRETCHED_SHARE
    ? ` Radiators cap thrust at ${Math.round(share * 100)} percent: engine open for ${held}`
    : ` Engine open for ${held}`;
  return `${label} - ${isMatch ? MATCH_TIP : BURN_TIPS[burn.kind]}`
    + (held === null ? '' : lasts)
    + (held === null ? ''
      : carriesTheClimb
        ? '. Cut the burn on the figure to finish.'
        : '. The trip prices it as one instant push.');
}

/** A burn's figure, with how long the engine is open for it when the thrust
 *  is known. */
export function burnFigure(burn: TransferBurn, accelMps2: number): string {
  const held = burnText(burn.dvKmS, accelMps2);
  return held === null ? kms(burn.dvKmS) : `${kms(burn.dvKmS)}, ${held}`;
}

/** A trip's own row tooltip: whose figures they are and what a click does. */
export function planTip(plan: TransferPlan, working: boolean): string {
  return `${plan.destinationName}: trip cost, crossing time and wait for the window. `
    + 'Click to pick this trip for GO.'
    + (working ? ` ${WORKING_TIP}` : '');
}

/** The flight plan's steps in the panel's own words. */
const PLAN_LABELS: Readonly<Record<AutopilotPlanStep['kind'], string>> = {
  wait: 'WAIT FOR WINDOW',
  depart: 'PUSH TO LEAVE',
  coast: 'COAST',
  fix: 'COURSE FIX',
  capture: 'BRAKE TO ARRIVE',
  arrive: 'ARRIVED',
};
const COAST_TIP = 'The engine is off and the ship falls along the crossing. The warp buttons are yours.';
const FIX_STEP_TIP = 'The autopilot pushes only if the ship has drifted off course. The SPEED CHANGE figure leaves this out; COURSE FIXES counts what it spends.';
const ARRIVE_STEP_TIP = 'The autopilot lets go and the stick is yours. The time is the planned end of the brake and can move by minutes.';
/** What a course fix spends and how long it takes, neither known ahead. */
const AS_NEEDED = 'as needed';

/** One step of the picked trip's flight plan, ready to paint. */
export interface PlanRow {
  readonly kind: AutopilotPlanStep['kind'];
  readonly label: string;
  /** Seconds from the epoch the plan was worked out at to the step's start. */
  readonly atS: number;
  readonly dvMps: number | null;
  readonly durationS: number;
  readonly at: string;
  readonly dv: string;
  readonly lasts: string;
  readonly how: string;
  readonly tip: string;
}

/** Every step GO flies on a trip, in order, off the autopilot's own schedule
 *  (`autopilotFlightPlan`), in the words and figures the panel prints, at the
 *  thrust a trip flies (`accelMps2`, already `share` of full). Empty
 *  where the ship's thrust is not known. */
export function planRows(plan: TransferPlan, accelMps2: number, share = 1): PlanRow[] {
  const steps = autopilotFlightPlan(plan, { accelMps2 });
  const epochJd = plan.departureEpochJd - plan.windowWaitDays;
  const name = plainBodyName(plan.destinationName);
  const departure = plan.burns.find((burn) => burn.kind === 'departure');
  const arrival = plan.burns.find((burn) => burn.kind === 'capture');
  const tilted = plan.planeTiltDeg !== null && plan.planeTiltDeg >= 0.05;
  const turn = (end: 'departure' | 'arrival'): string => (tilted && plan.planeChangeEnd === end
    ? `, turning into ${name}'s plane` : '');
  let fixes = 0;
  return steps.map((step) => {
    const atS = (step.startJd - epochJd) * 86400;
    const durationS = (step.endJd - step.startJd) * 86400;
    const burn = step.kind === 'depart' ? departure : step.kind === 'capture' ? arrival : undefined;
    let label = PLAN_LABELS[step.kind];
    let how = '';
    let tipText = '';
    switch (step.kind) {
      case 'wait':
        how = 'engine off';
        tipText = WAIT_TIP;
        break;
      case 'depart':
        how = burn === undefined ? '' : `${SENSE_WORDS[burn.sense]}${turn('departure')}`;
        tipText = burn === undefined ? '' : burnTip(burn, accelMps2, share);
        break;
      case 'coast':
        how = 'engine off';
        tipText = COAST_TIP;
        break;
      case 'fix':
        fixes += 1;
        label = `${label} ${fixes}`;
        how = 'a push only if the course has drifted';
        tipText = FIX_STEP_TIP;
        break;
      case 'capture':
        how = burn === undefined ? ''
          : `${burn.frame === 'heliocentric' ? 'match velocity' : 'capture'}, ${SENSE_WORDS[burn.sense]}${turn('arrival')}`;
        tipText = burn === undefined ? '' : burnTip(burn, accelMps2, share);
        break;
      case 'arrive':
        how = arrival === undefined ? `at ${name}, the stick is yours`
          : arrival.frame === 'heliocentric' ? `stopped beside ${name}, the stick is yours`
            : `in orbit over ${name}, the stick is yours`;
        tipText = ARRIVE_STEP_TIP;
        break;
    }
    const dv = step.kind === 'fix' ? AS_NEEDED : step.dvMps === null ? NOTHING : kms(step.dvMps / 1000);
    const lasts = step.kind === 'fix' ? AS_NEEDED
      : step.kind === 'arrive' ? NOTHING
        : burn !== undefined ? burnText(burn.dvKmS, accelMps2) ?? NOTHING
          : days(durationS / 86400);
    return {
      kind: step.kind, label, atS, dvMps: step.dvMps, durationS,
      at: waitText(atS / 86400), dv, lasts, how, tip: tipText,
    };
  });
}

/**
 * The control (`?dvplandropstep=1`): both flight-plan lists leave out the
 * plan's second step. tools/preview/dvmap-panel-fit.mjs's plan leg has to
 * catch the drawn list and the plan data parting.
 */
const DROP_PLAN_STEP = TEST_SWITCHES && new URLSearchParams(location.search).get('dvplandropstep') === '1';

export type PlanCol = 'step' | 'at' | 'dv' | 'lasts' | 'how';

/**
 * The dated step WARP TO NEXT jumps the clock to, in the flight plan's own
 * words. A course fix takes the same numbered label the FLIGHT PLAN list
 * gives it (`planRows`), matched by its own scheduled time against the live
 * countdown rather than counted again here - a second count could disagree
 * with the list's own and name the wrong fix. Falls back to the bare step
 * name where there is no plan or thrust to read a schedule off.
 */
export function nextStepLabel(
  skipStep: AutopilotStep, nextEventS: number, plan: TransferPlan | null,
  accelMps2: number, nowJd: number,
): string {
  if (skipStep !== 'course-fix' || plan === null || !(accelMps2 > 0) || !Number.isFinite(nowJd)) {
    return STEP_WORDS[skipStep];
  }
  const epochJd = plan.departureEpochJd - plan.windowWaitDays;
  const targetS = (nowJd - epochJd) * 86400 + nextEventS;
  let best: PlanRow | null = null;
  let bestDiffS = Infinity;
  for (const row of planRows(plan, accelMps2)) {
    if (row.kind !== 'fix') continue;
    const diffS = Math.abs(row.atS - targetS);
    if (diffS < bestDiffS) {
      bestDiffS = diffS;
      best = row;
    }
  }
  if (best === null) return STEP_WORDS[skipStep];
  return best.label.charAt(0) + best.label.slice(1).toLowerCase();
}

/** Paint a flight plan into `box`, one `.dv-plan-row` per step, on the rows
 *  already there where it can: the map repaints on every survey chunk, and a
 *  tooltip the pointer is on should not lose its row to that. */
export function paintPlan(box: HTMLElement, rows: readonly PlanRow[], cols: readonly PlanCol[]): void {
  const shown = DROP_PLAN_STEP ? rows.filter((_, i) => i !== 1) : rows;
  while (box.children.length > shown.length) box.lastElementChild?.remove();
  shown.forEach((row, i) => {
    let el = box.children[i] as HTMLElement | undefined;
    if (el === undefined) {
      el = document.createElement('div');
      el.className = 'dv-plan-row';
      for (const col of cols) {
        const span = document.createElement('span');
        span.dataset['col'] = col;
        el.append(span);
      }
      box.append(el);
    }
    el.dataset['step'] = row.kind;
    el.dataset['at'] = String(row.atS);
    tip(el, row.tip);
    const text: Readonly<Record<PlanCol, string>> = {
      step: row.label, at: row.at, dv: row.dv, lasts: row.lasts, how: row.how,
    };
    for (const span of Array.from(el.children) as HTMLElement[]) {
      const value = text[span.dataset['col'] as PlanCol];
      if (span.textContent !== value) span.textContent = value;
    }
  });
}
